import { useEffect, useRef, useState, type PointerEvent } from 'react';
import type { TestimonialGridContent } from '~/content/types';
import { defaultTestimonials } from '~/content/shared';
import { Section, SectionHeading } from '~/components/layout';
import { ResponsiveText, SmartImage } from '~/components/ui';

// Each card's white cover slides up over this duration, staggered left→right.
const REVEAL_MS = 900;
const STAGGER_MS = 120;

/** "Built for people just like you" image cards (Figma 27:6483). */
export function TestimonialGrid({
  content = defaultTestimonials,
}: {
  content?: TestimonialGridContent;
}) {
  const gridRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ down: false, startX: 0, startLeft: 0 });
  const [revealed, setRevealed] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  // Mouse click-and-drag horizontal scroll on the mobile track — mirrors the
  // reviews carousel (QuoteTestimonials). Native touch-swipe/momentum still
  // works because we only adjust scrollLeft; on desktop the grid has no
  // horizontal overflow so these handlers are inert.
  const onPointerDown = (e: PointerEvent) => {
    const el = gridRef.current;
    if (!el) return;
    drag.current = { down: true, startX: e.clientX, startLeft: el.scrollLeft };
    el.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent) => {
    const el = gridRef.current;
    if (!el || !drag.current.down) return;
    el.scrollLeft = drag.current.startLeft - (e.clientX - drag.current.startX);
  };

  const endDrag = (e: PointerEvent) => {
    drag.current.down = false;
    gridRef.current?.releasePointerCapture?.(e.pointerId);
  };

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setReduceMotion(true);
      setRevealed(true);
      return;
    }
    const el = gridRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    // 100px top pairs with the investors grid's 100px bottom for a 200px gap;
    // the default 56px bottom pairs with the FAQ's 144px top for 200px below.
    <Section className="!pt-[100px]">
      <SectionHeading title={content.title} />
      {/* Mobile: horizontal slider matching the reviews (QuoteTestimonials)
          track — same gap-5, 292px card width, and hidden scrollbar.
          Desktop (md+): the original 9-column grid, unchanged. */}
      <div
        ref={gridRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDragStart={(e) => e.preventDefault()}
        className="mt-10 flex cursor-grab gap-5 overflow-x-auto select-none [-ms-overflow-style:none] [scrollbar-width:none] active:cursor-grabbing [&::-webkit-scrollbar]:hidden md:grid md:cursor-default md:grid-cols-9 md:gap-x-5 md:gap-y-6 md:overflow-visible md:active:cursor-default">
        {content.items.map((item, i) => (
          <figure
            key={item.label}
            className="relative h-[460px] w-[292px] shrink-0 overflow-hidden md:col-span-3 md:h-[640px] md:w-auto">
            <SmartImage image={item.image} className="h-full w-full" />
            <span className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <figcaption className="absolute bottom-8 start-8 z-10 text-h3 text-white md:bottom-10 md:start-10">
              <ResponsiveText text={item.label} mobileText={item.mobileLabel} />
            </figcaption>
            {/* White cover that slides up to reveal the card, staggered left→right. */}
            <span
              aria-hidden
              className="absolute inset-0 z-20 bg-white"
              style={{
                transitionProperty: 'transform',
                transitionTimingFunction: 'var(--ease-out-strong, cubic-bezier(0.16, 1, 0.3, 1))',
                transitionDuration: reduceMotion ? '0ms' : `${REVEAL_MS}ms`,
                transitionDelay: reduceMotion ? '0ms' : revealed ? `${i * STAGGER_MS}ms` : '0ms',
                transform: revealed ? 'translateY(-100%)' : 'translateY(0)',
              }}
            />
          </figure>
        ))}
      </div>
    </Section>
  );
}
